import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import './SaveAsNewProtocolModal.scss';

export const PROTOCOL_NAME_MAX_LENGTH = 50;

export type ProtocolNameValidationError = 'duplicate';

export const isDuplicateProtocolName = (
  name: string,
  existingNames: string[]
): boolean => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return existingNames.some(
    (existing) => existing.trim().toLowerCase() === normalized
  );
};

export const getProtocolNameValidationError = (
  name: string,
  existingNames: string[],
  checkDuplicate = false
): ProtocolNameValidationError | null => {
  if (checkDuplicate && isDuplicateProtocolName(name, existingNames)) {
    return 'duplicate';
  }

  return null;
};

const DUPLICATE_PROTOCOL_NAME_ERROR_CODE = '94001000025';

const hasDuplicateProtocolNameErrorCode = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  return value.includes(DUPLICATE_PROTOCOL_NAME_ERROR_CODE);
};

export const getSaveAsNewProtocolErrorMessage = (error: unknown): string => {
  const duplicateMessage = t(
    'ai.review.jobDetailsTab.saveAsNewProtocolDuplicateError'
  );
  if (!error || typeof error !== 'object') {
    return duplicateMessage;
  }

  const apiError = error as {
    code?: unknown;
    response?: {data?: {detail?: unknown; message?: unknown}};
    message?: unknown;
  };
  const detail = apiError.response?.data?.detail;
  const message = apiError.response?.data?.message;

  if (
    [detail, message, apiError.message, apiError.code].some(
      hasDuplicateProtocolNameErrorCode
    )
  ) {
    return duplicateMessage;
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (typeof apiError.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }

  return duplicateMessage;
};

interface SaveAsNewProtocolModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (protocolName: string) => void;
  errorMessage?: string | null;
  description?: string;
  textFieldLabel?: string;
}

const SaveAsNewProtocolModal = ({
  open,
  onClose,
  onSave,
  errorMessage = null,
  description = t('ai.review.jobDetailsTab.saveAsNewProtocolModalDescription'),
  textFieldLabel = t('ai.review.jobDetailsTab.saveAsNewProtocolNameLabel'),
}: SaveAsNewProtocolModalProps) => {
  const [protocolName, setProtocolName] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setProtocolName('');
    setServerError(null);
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  useEffect(() => {
    setServerError(errorMessage ?? null);
  }, [errorMessage]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProtocolName(event.target.value);
    setServerError(null);
  };

  const handleSave = () => {
    const trimmedName = protocolName.trim();
    if (!trimmedName) {
      return;
    }

    onSave(trimmedName);
  };

  const helperText = useMemo(() => {
    if (serverError) {
      return serverError;
    }
    return `${protocolName.length}/${PROTOCOL_NAME_MAX_LENGTH}`;
  }, [protocolName.length, serverError]);

  const isSaveDisabled = !protocolName.trim();

  return (
    <ArrModal
      className="arr-save-as-new-protocol-modal"
      showModal={open}
      onClose={handleClose}
      showClose={false}
      title={t('ai.review.jobDetailsTab.saveAsNewProtocolModalTitle')}
      description={description}
      aria-label={t('ai.review.jobDetailsTab.saveAsNewProtocolModalTitle')}
      dataTestId="save-as-new-protocol-modal"
      buttons={[
        {
          id: 'cancel-btn',
          label: t('ai.review.leftPanel.cancelBtn'),
          onClick: handleClose,
          variant: 'secondary',
          dataTestId: 'save-as-new-protocol-cancel-button',
        },
        {
          id: 'save-btn',
          label: t('ai.review.jobDetailsTab.saveAsNewProtocol'),
          onClick: handleSave,
          variant: 'primary',
          disabled: isSaveDisabled,
          dataTestId: 'save-as-new-protocol-save-button',
        },
      ]}
    >
      <Box className="save-as-new-protocol-name-field">
        <ArrTextField
          label={textFieldLabel}
          value={protocolName}
          onChange={handleChange}
          state={serverError ? 'error' : 'enabled'}
          showErrorIcon={Boolean(serverError)}
          supportText={helperText}
          helperText={helperText}
          dataTestId="save-as-new-protocol-name-input"
          slotProps={{
            input: {
              inputProps: {maxLength: PROTOCOL_NAME_MAX_LENGTH},
            },
            formHelperText: {
              'aria-label': serverError
                ? serverError
                : `${protocolName.length} ${t('ai.review.jobDetailsTab.saveAsNewProtocolOutOf50Characters')}`,
            },
          }}
        />
      </Box>
    </ArrModal>
  );
};

export default SaveAsNewProtocolModal;
